import type { TacticalFeature } from "@/services/tactical.service";
import { circlePolygon } from "@/tactical/plugins/rangeRingGeometry";
import type { LngLat } from "./snapPoint";

/**
 * Буфер фичи в метрах → кольцо координат полигона.
 * Point — круг; line/polygon — convex hull кругов по вершинам (MVP без обязательного @turf в runtime).
 */
export function buildBufferPolygon(
  feature: TacticalFeature,
  radiusM: number,
): LngLat[] | null {
  const r = Math.max(1, radiusM);
  const g = feature.geometry;
  if (!g) return null;

  if (g.type === "Point") {
    const f = circlePolygon(g.coordinates[0], g.coordinates[1], r, 64);
    return f.geometry.coordinates[0].map((c) => [c[0], c[1]] as LngLat);
  }

  const verts: LngLat[] = [];
  if (g.type === "LineString") {
    for (const c of g.coordinates) verts.push([c[0], c[1]]);
  } else if (g.type === "Polygon") {
    for (const c of g.coordinates[0] ?? []) verts.push([c[0], c[1]]);
  } else {
    return null;
  }
  if (verts.length === 0) return null;

  const cloud: LngLat[] = [];
  for (const v of verts) {
    const circ = circlePolygon(v[0], v[1], r, 24);
    for (const c of circ.geometry.coordinates[0]) {
      cloud.push([c[0], c[1]]);
    }
  }
  return convexHull(cloud);
}

function convexHull(points: LngLat[]): LngLat[] | null {
  const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (pts.length < 3) return null;
  const cross = (o: LngLat, a: LngLat, b: LngLat) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: LngLat[] = [];
  for (const p of pts) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: LngLat[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  const hull = lower.concat(upper);
  return hull.length >= 3 ? hull : null;
}
